import React, {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useState,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { useOnboardingViewsRecordAnalyticsEventMutation } from "@/store/airloom-generated-rtk";
import {
  setFunnel,
  setStep,
  gaEvent,
  GA4_EVENTS,
  EVENT_CATEGORY,
} from "@/lib/analytics";

// Storage keys
const STORAGE_KEY = "airloom-onboarding-state";

// Step configuration
interface StepConfig {
  id: string;
  title: string;
  route: string;
  allowBack: boolean;
  condition?: (data: OnboardingFormData) => boolean;
}

const STEP_CONFIG: StepConfig[] = [
  {
    id: "businessType",
    title: "Business Type",
    route: "business-type",
    allowBack: false,
  },
  {
    id: "videoSystem",
    title: "Video System",
    route: "video-system",
    allowBack: true,
  },
  { id: "country", title: "Country", route: "country", allowBack: true },
  {
    id: "state",
    title: "State",
    route: "state",
    allowBack: true,
    condition: (data) => data.country === "United States",
  },
  {
    id: "interestReason",
    title: "Interest Reason",
    route: "interest-reason",
    allowBack: true,
  },
  {
    id: "locationCount",
    title: "How many physical locations?",
    route: "location-count",
    allowBack: true,
  },
  {
    id: "cameraCount",
    title: "Camera Count",
    route: "camera-count",
    allowBack: true,
  },
  {
    id: "awaitingReturn",
    title: "Completing Account Setup...",
    route: "awaiting-return",
    allowBack: false,
  },
  { id: "pricing", title: "Choose Plan", route: "pricing", allowBack: true },
  {
    id: "paymentProcessing",
    title: "Processing Your Subscription...",
    route: "subscription-processing",
    allowBack: false,
  },
  {
    id: "waitlist",
    title: "Join Waitlist",
    route: "waitlist",
    allowBack: true,
  },
  {
    id: "enterpriseFlow",
    title: "Enterprise Consultation",
    route: "enterprise",
    allowBack: true,
  },
  {
    id: "waitlistCompleted",
    title: "You're on the waitlist!",
    route: "waitlist-completed",
    allowBack: false,
  },
  {
    id: "enterpriseCompleted",
    title: "Consultation scheduled!",
    route: "enterprise-completed",
    allowBack: false,
  },
  {
    id: "onboardingError",
    title: "Something went wrong",
    route: "error",
    allowBack: true,
  },
];

const ONBOARDING_STEPS = {
  // For analytics
  ...STEP_CONFIG.reduce(
    (acc, step) => {
      const key = step.id.replace(/([a-z])([A-Z])/g, "$1_$2").toUpperCase();
      acc[key] = step.id;
      return acc;
    },
    {} as Record<string, string>,
  ),
  // Analytics-only events
  WAITLIST_SUBMISSION: "waitlistSubmission",
  ENTERPRISE_SUBMISSION: "enterpriseSubmission",
  COMPLETED: "completed",
  ONBOARDING_ERROR: "onboardingError",
};

// Form data structure
interface OnboardingFormData {
  businessType: string;
  videoSystemBrand: string;
  country: string;
  state: string;
  interestReason: string[];
  numberOfLocations: number;
  camerasPerLocation: number;
  businessId?: string;
  isSmb?: boolean;
  selectedTier?: "basic" | "pro" | "enterprise";
  waitlistReason?: "UNSUPPORTED_SYSTEM" | "UNSUPPORTED_COUNTRY";
  // Additional fields for different flows
  contactInfo?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
  };
  enterpriseInfo?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    company?: string;
    title?: string;
  };
}

// Context state
interface OnboardingState {
  currentStep: string;
  formData: OnboardingFormData;
  completedSteps: string[];
  isLoading: boolean;
  error: string | null;
}

// Context API
interface OnboardingContextType {
  // State
  currentStep: string;
  formData: OnboardingFormData;
  completedSteps: string[];
  isLoading: boolean;
  error: string | null;

  // Actions
  updateFormData: (data: Partial<OnboardingFormData>) => void;
  nextStep: () => void;
  previousStep: () => void;
  goToStep: (step: string) => void;
  clearData: () => void;

  // Computed properties
  canGoBack: boolean;
  progress: number;
  stepTitle: string;
  showProgress: boolean;
  isFinalState: boolean;
  ctaText: string;
}

// Create context
const OnboardingContext = createContext<OnboardingContextType | undefined>(
  undefined,
);

// Default form data
const DEFAULT_FORM_DATA: OnboardingFormData = {
  businessType: "",
  videoSystemBrand: "",
  country: "",
  state: "",
  interestReason: [],
  numberOfLocations: 0,
  camerasPerLocation: 0,
};

// Helper functions
const isSmbCustomer = (locationCount: number, cameraCount: number): boolean => {
  return locationCount <= 2 && cameraCount <= 32;
};

const getNextStep = (
  currentStep: string,
  formData: OnboardingFormData,
): string => {
  const currentIndex = STEP_CONFIG.findIndex((step) => step.id === currentStep);

  // Handle special branching logic
  if (currentStep === "videoSystem") {
    if (formData.waitlistReason === "UNSUPPORTED_SYSTEM") {
      return "waitlist";
    }
  }

  if (currentStep === "country") {
    if (formData.waitlistReason === "UNSUPPORTED_COUNTRY") {
      return "waitlist";
    }
  }

  if (currentStep === "cameraCount") {
    const isSmb = isSmbCustomer(
      formData.numberOfLocations,
      formData.camerasPerLocation,
    );

    if (!isSmb) {
      return "enterpriseFlow";
    } else {
      // Instead of account creation, go to pricing
      return "pricing";
    }
  }

  if (currentStep === "awaitingReturn") {
    // After awaiting, the user is authenticated and business is created,
    // now we proceed based on the selected tier.
    // The actual redirection to Stripe happens in the AwaitingReturnStep component.
    if (formData.selectedTier === "enterprise") {
      return "enterpriseCompleted";
    }
    // For basic/pro, the AwaitingReturnStep handles the redirect.
    // If it fails, it stays on that step. If successful, user is off-site.
    // If for some reason it completes without redirect, we land here.
    return "completed";
  }

  if (currentStep === "pricing") {
    // After pricing, user goes to account creation, then awaitingReturn
    return "awaitingReturn";
  }

  if (currentStep === "paymentProcessing") {
    return "completed";
  }

  if (currentStep === "waitlist") {
    return "waitlistCompleted";
  }

  if (currentStep === "enterpriseFlow") {
    return "enterpriseCompleted";
  }

  // For other steps, find the next valid step
  for (let i = currentIndex + 1; i < STEP_CONFIG.length; i++) {
    const step = STEP_CONFIG[i];
    if (!step.condition || step.condition(formData)) {
      return step.id;
    }
  }

  return currentStep;
};

const getPreviousStep = (
  currentStep: string,
  formData: OnboardingFormData,
): string => {
  const currentIndex = STEP_CONFIG.findIndex((step) => step.id === currentStep);

  if (currentStep === "waitlist") {
    if (formData.waitlistReason === "UNSUPPORTED_SYSTEM") {
      return "videoSystem";
    }
    if (formData.waitlistReason === "UNSUPPORTED_COUNTRY") {
      return "country";
    }
    return "cameraCount";
  }

  if (currentStep === "enterpriseFlow") {
    return "pricing";
  }

  if (currentStep === "pricing") {
    return "cameraCount";
  }

  if (currentStep === "awaitingReturn") {
    return "pricing";
  }

  // For other steps, find the previous valid step
  for (let i = currentIndex - 1; i >= 0; i--) {
    const step = STEP_CONFIG[i];
    if (!step.condition || step.condition(formData)) {
      return step.id;
    }
  }

  return currentStep;
};

const calculateProgress = (currentStep: string): number => {
  const progressMap: Record<string, number> = {
    businessType: 8,
    videoSystem: 15,
    country: 23,
    state: 31,
    interestReason: 38,
    locationCount: 46,
    cameraCount: 54,
    pricing: 62,
    awaitingReturn: 85,
    paymentProcessing: 92,
    completed: 100,
    waitlist: 62,
    enterpriseFlow: 62,
    waitlistCompleted: 100,
    enterpriseCompleted: 100,
  };

  return progressMap[currentStep] || 0;
};

// Storage utilities
const saveToStorage = (state: OnboardingState): void => {
  try {
    const dataWithTimestamp = {
      ...state,
      timestamp: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(dataWithTimestamp));
  } catch (error) {
    console.warn("Failed to save onboarding state:", error);
  }
};

const loadFromStorage = (): OnboardingState | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const parsed = JSON.parse(stored);

    if (
      parsed &&
      typeof parsed === "object" &&
      parsed.currentStep &&
      parsed.formData
    ) {
      // Check if data has timestamp (new format)
      if (!parsed.timestamp) {
        // Old data without timestamp, clear it and start fresh
        clearStorage();
        return null;
      }

      // Check if data is older than 24 hours
      const now = Date.now();
      const twentyFourHours = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

      if (now - parsed.timestamp > twentyFourHours) {
        // Data is expired, clear it and return null
        clearStorage();
        return null;
      }

      return {
        currentStep: parsed.currentStep,
        formData: { ...DEFAULT_FORM_DATA, ...parsed.formData },
        completedSteps: parsed.completedSteps || [],
        isLoading: false,
        error: null,
      };
    }

    return null;
  } catch (error) {
    console.warn("Failed to load onboarding state:", error);
    return null;
  }
};

const clearStorage = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn("Failed to clear onboarding state:", error);
  }
};

// Provider component
export const OnboardingProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();

  const [state, setState] = useState<OnboardingState>({
    currentStep: "businessType",
    formData: DEFAULT_FORM_DATA,
    completedSteps: [],
    isLoading: false,
    error: null,
  });

  const [hasInitialized, setHasInitialized] = useState(false);
  const [hasHandledStripeReturn, setHasHandledStripeReturn] = useState(false);
  const [hasTrackedWaitlist, setHasTrackedWaitlist] = useState(false);

  const [recordAnalyticsEvent] =
    useOnboardingViewsRecordAnalyticsEventMutation();

  // Initialize from storage on mount
  useEffect(() => {
    if (!hasInitialized) {
      const loadedState = loadFromStorage();
      const hasAuthTokens = document.cookie.includes("sFrontToken");
      const returnedFromAuth =
        window.location.pathname.includes("/onboarding") && hasAuthTokens;

      // Initialize GA4 funnel tracking
      setFunnel("onboarding_flow");

      if (loadedState) {
        // Check if we're on subscription-processing route (Stripe return)
        if (window.location.pathname.includes("subscription-processing")) {
          setState({ ...loadedState, currentStep: "paymentProcessing" });
        } else if (
          returnedFromAuth &&
          loadedState.currentStep === "pricing" // User was sent to sign up from pricing
        ) {
          setState({ ...loadedState, currentStep: "awaitingReturn" });
        } else {
          setState(loadedState);
          // Navigate to the step the user was on
          const stepConfig = STEP_CONFIG.find(
            (s) => s.id === loadedState.currentStep,
          );
          if (stepConfig) {
            navigate(`/onboarding/${stepConfig.route}`);
          }
        }
      } else {
        // New onboarding session - track funnel start
        gaEvent(GA4_EVENTS.BEGIN_ONBOARDING, {
          event_category: EVENT_CATEGORY.ONBOARDING,
          step: 1,
          step_name: ONBOARDING_STEPS.BUSINESS_TYPE,
        });
        setStep(1, ONBOARDING_STEPS.BUSINESS_TYPE);
      }
      setHasInitialized(true);
    }
  }, [hasInitialized, navigate]);

  // Handle Stripe return
  useEffect(() => {
    if (!hasInitialized || hasHandledStripeReturn) return;

    const urlParams = new URLSearchParams(window.location.search);
    const returnedFromStripe = urlParams.get("success");

    if (returnedFromStripe) {
      setState((prev) => ({ ...prev, currentStep: "paymentProcessing" }));
      navigate("/onboarding/subscription-processing");
      setHasHandledStripeReturn(true);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [hasInitialized, hasHandledStripeReturn, navigate]);

  // Track onboarding analytics
  const trackOnboardingEvent = useCallback(
    async (
      eventType: "waitlist" | "abandon",
      additionalData: Record<string, any> = {},
    ) => {
      try {
        await recordAnalyticsEvent({
          onboardingAnalyticsInSchema: {
            event_type: eventType,
            timestamp: new Date().toISOString(),
            video_system: state.formData.videoSystemBrand,
            business_type: state.formData.businessType,
            country: state.formData.country,
            state: state.formData.state,
            interest_reasons: state.formData.interestReason,
            location_count: state.formData.numberOfLocations,
            camera_count: state.formData.camerasPerLocation,
            waitlist_reason: state.formData.waitlistReason,
            last_step: state.currentStep,
            form_data: state.formData,
            ...additionalData,
          },
        }).unwrap();
      } catch (error) {
        console.error("Failed to track onboarding event:", error);
      }
    },
    [state.formData, state.currentStep, recordAnalyticsEvent],
  );

  // Track waitlist events
  useEffect(() => {
    if (
      hasInitialized &&
      state.currentStep === "waitlist" &&
      !hasTrackedWaitlist
    ) {
      trackOnboardingEvent("waitlist");
      setHasTrackedWaitlist(true);
    }
  }, [
    hasInitialized,
    state.currentStep,
    hasTrackedWaitlist,
    trackOnboardingEvent,
  ]);

  // Track abandon events on page unload
  useEffect(() => {
    if (!hasInitialized) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const finalStates = [
        "completed",
        "waitlistCompleted",
        "enterpriseCompleted",
        "waitlist", // Don't track abandon if already on waitlist
      ];
      const isInProgress = !finalStates.includes(state.currentStep);

      if (
        isInProgress &&
        Object.keys(state.formData).some(
          (key) =>
            state.formData[key as keyof OnboardingFormData] !==
            DEFAULT_FORM_DATA[key as keyof OnboardingFormData],
        )
      ) {
        e.preventDefault();
        trackOnboardingEvent("abandon");
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasInitialized, state.currentStep, state.formData, trackOnboardingEvent]);

  // Track step views in GA4
  useEffect(() => {
    if (hasInitialized && state.currentStep) {
      const stepIndex = STEP_CONFIG.findIndex(
        (s) => s.id === state.currentStep,
      );
      const stepNumber = stepIndex + 1;

      // Track step view event
      gaEvent(GA4_EVENTS.VIEW_STEP, {
        event_category: EVENT_CATEGORY.ONBOARDING,
        step: stepNumber,
        step_name: state.currentStep,
      });
    }
  }, [state.currentStep, hasInitialized]);

  // Auto-save to storage
  useEffect(() => {
    if (hasInitialized) {
      const finalStates = [
        "completed",
        "waitlistCompleted",
        "enterpriseCompleted",
      ];
      // Don't save onboardingError to localStorage to prevent stuck loops
      if (finalStates.includes(state.currentStep)) {
        clearStorage();
      } else if (state.currentStep !== "onboardingError") {
        saveToStorage(state);
      }
    }
  }, [state, hasInitialized]);

  const updateFormData = useCallback((data: Partial<OnboardingFormData>) => {
    setState((prev) => ({
      ...prev,
      formData: { ...prev.formData, ...data },
    }));
  }, []);

  const nextStep = useCallback(() => {
    setState((prev) => {
      const nextStepId = getNextStep(prev.currentStep, prev.formData);
      const newCompletedSteps = [...prev.completedSteps];
      if (!newCompletedSteps.includes(prev.currentStep)) {
        newCompletedSteps.push(prev.currentStep);
      }

      // Track step progression in GA4
      const stepIndex = STEP_CONFIG.findIndex((s) => s.id === nextStepId);
      const stepNumber = stepIndex + 1;

      // Track step completion event
      gaEvent(GA4_EVENTS.COMPLETE_STEP, {
        event_category: EVENT_CATEGORY.ONBOARDING,
        previous_step: prev.currentStep,
        next_step: nextStepId,
        step: stepNumber,
        step_name: nextStepId,
        ...prev.formData,
      });

      // Update funnel step tracking
      setStep(stepNumber, nextStepId);

      // Track specific conversion events
      if (nextStepId === ONBOARDING_STEPS.WAITLIST_COMPLETED) {
        gaEvent(GA4_EVENTS.JOIN_WAITLIST, {
          event_category: EVENT_CATEGORY.ONBOARDING,
          waitlist_reason: prev.formData.waitlistReason,
          ...prev.formData,
        });
        // Clear storage when completing waitlist
        clearStorage();
      } else if (nextStepId === ONBOARDING_STEPS.ENTERPRISE_COMPLETED) {
        gaEvent(GA4_EVENTS.ENTERPRISE_INQUIRY, {
          event_category: EVENT_CATEGORY.ONBOARDING,
          ...prev.formData,
        });
        // Clear storage when completing enterprise flow
        clearStorage();
      } else if (nextStepId === ONBOARDING_STEPS.PAYMENT_PROCESSING) {
        gaEvent(GA4_EVENTS.BEGIN_CHECKOUT, {
          event_category: EVENT_CATEGORY.ONBOARDING,
          tier: prev.formData.selectedTier,
          ...prev.formData,
        });
      } else if (nextStepId === ONBOARDING_STEPS.COMPLETED) {
        gaEvent(GA4_EVENTS.COMPLETE_ONBOARDING, {
          event_category: EVENT_CATEGORY.ONBOARDING,
          ...prev.formData,
        });
        // Clear storage when completing onboarding
        clearStorage();
      }

      // Navigate to the next step
      const nextStepConfig = STEP_CONFIG.find((s) => s.id === nextStepId);
      if (nextStepConfig) {
        navigate(`/onboarding/${nextStepConfig.route}`);
      }

      return {
        ...prev,
        currentStep: nextStepId,
        completedSteps: newCompletedSteps,
      };
    });
  }, [navigate]);

  const previousStep = useCallback(() => {
    setState((prev) => {
      const prevStepId = getPreviousStep(prev.currentStep, prev.formData);

      // Track back navigation in GA4
      const stepIndex = STEP_CONFIG.findIndex((s) => s.id === prevStepId);
      const stepNumber = stepIndex + 1;

      gaEvent(GA4_EVENTS.NAVIGATE_BACK, {
        event_category: EVENT_CATEGORY.ONBOARDING,
        from_step: prev.currentStep,
        to_step: prevStepId,
        step: stepNumber,
        step_name: prevStepId,
      });

      // Update funnel step tracking
      setStep(stepNumber, prevStepId);

      // Navigate to the previous step
      const prevStepConfig = STEP_CONFIG.find((s) => s.id === prevStepId);
      if (prevStepConfig) {
        navigate(`/onboarding/${prevStepConfig.route}`);
      }

      return { ...prev, currentStep: prevStepId };
    });
  }, [navigate]);

  const goToStep = useCallback(
    (step: string) => {
      setState((prev) => ({ ...prev, currentStep: step }));

      // Navigate to the step route
      const stepConfig = STEP_CONFIG.find((s) => s.id === step);
      if (stepConfig) {
        navigate(`/onboarding/${stepConfig.route}`);
      }
    },
    [navigate],
  );

  const clearData = useCallback(() => {
    setState({
      currentStep: "businessType",
      formData: DEFAULT_FORM_DATA,
      completedSteps: [],
      isLoading: false,
      error: null,
    });
    clearStorage();
  }, []);

  const currentStepConfig = STEP_CONFIG.find(
    (step) => step.id === state.currentStep,
  );
  const canGoBack = currentStepConfig?.allowBack ?? false;
  const progress = calculateProgress(state.currentStep);
  const stepTitle = currentStepConfig?.title || "Getting started";

  const showProgressSteps = [
    "businessType",
    "videoSystem",
    "country",
    "state",
    "interestReason",
    "locationCount",
    "cameraCount",
    "pricing",
    "awaitingReturn",
    "paymentProcessing",
    "waitlist",
    "enterpriseFlow",
  ];
  const showProgress = showProgressSteps.includes(state.currentStep);

  const finalStates = ["completed", "waitlistCompleted", "enterpriseCompleted"];
  const isFinalState = finalStates.includes(state.currentStep);

  const ctaMap: Record<string, string> = {
    awaitingReturn: "Please wait...",
    pricing: "Select Plan",
    paymentProcessing: "Please wait...",
    waitlist: "Join Waitlist",
    enterpriseFlow: "Schedule Consultation",
    completed: "Get Started",
    waitlistCompleted: "Close",
    enterpriseCompleted: "Close",
  };
  const ctaText = ctaMap[state.currentStep] || "Continue";

  const value: OnboardingContextType = {
    currentStep: state.currentStep,
    formData: state.formData,
    completedSteps: state.completedSteps,
    isLoading: state.isLoading,
    error: state.error,
    updateFormData,
    nextStep,
    previousStep,
    goToStep,
    clearData,
    canGoBack,
    progress,
    stepTitle,
    showProgress,
    isFinalState,
    ctaText,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = (): OnboardingContextType => {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error("useOnboarding must be used within an OnboardingProvider");
  }
  return context;
};

// Legacy hooks for backward compatibility
export const useCurrentStep = (): string => {
  const { currentStep } = useOnboarding();
  return currentStep;
};

export const useCanGoBack = (): boolean => {
  const { canGoBack } = useOnboarding();
  return canGoBack;
};

export const useProgress = (): number => {
  const { progress } = useOnboarding();
  return progress;
};

export const useShowProgress = (): boolean => {
  const { showProgress } = useOnboarding();
  return showProgress;
};

export const useStepTitle = (): string => {
  const { stepTitle } = useOnboarding();
  return stepTitle;
};

export const useIsFinalState = (): boolean => {
  const { isFinalState } = useOnboarding();
  return isFinalState;
};

export const useCtaText = (): string => {
  const { ctaText } = useOnboarding();
  return ctaText;
};

export default OnboardingContext;
